import React, { useState, useRef } from 'react';
import { UploadCloudIcon, FileIcon, XIcon, CheckCircleIcon, AlertCircleIcon, DownloadIcon } from 'lucide-react';
import Card from '../../components/ui/Card';
import Table from '../../components/ui/Table';
import StatusBadge from '../../components/ui/StatusBadge';
import Modal from '../../components/ui/Modal';
const SCHEME_LOGOS: Record<string, string> = {
  Visa: 'https://cdn.magicpatterns.com/uploads/8xq2uxNNNydtTwxH2KaA3P/Visa_Logo.png',
  Mastercard: 'https://cdn.magicpatterns.com/uploads/uhhVfXBtdJ27miMoBQB5UQ/MasterCard_Logo.png',
  UnionPay: 'https://cdn.magicpatterns.com/uploads/f6k19YT6hK7xuaUtj73TGV/UnionPay_Logo.png',
  PayPak: 'https://cdn.magicpatterns.com/uploads/5aESH9zRKbVPLTL9anhfA2/PayPak_Logo.png'
};
const MERCHANT_NAMES = ['Karachi Electronics', 'Lahore Textiles', 'Islamabad Foods', 'Peshawar Traders', 'Faisalabad Garments', 'Multan Jewelers', 'Rawalpindi Motors', 'Sialkot Sports'];
const generateMockRecords = (scheme: string, count: number = 10) => {
  const records = [];
  const getPrefix = (s: string) => {
    switch (s) {
      case 'Visa':
        return '4';
      case 'Mastercard':
        return '5';
      case 'UnionPay':
        return '6';
      case 'PayPak':
        return '2';
      default:
        return '4';
    }
  };
  const getCasePrefix = (s: string) => {
    switch (s) {
      case 'Visa':
        return 'VISA';
      case 'Mastercard':
        return 'MC';
      case 'UnionPay':
        return 'UP';
      case 'PayPak':
        return 'PP';
      default:
        return 'CASE';
    }
  };
  for (let i = 1; i <= count; i++) {
    const prefix = getPrefix(scheme);
    const casePrefix = getCasePrefix(scheme);
    records.push({
      rowNum: i,
      caseId: `${casePrefix}-2023-${Math.floor(100 + Math.random() * 900)}`,
      arn: `74${Math.floor(1000000000000000000000 + Math.random() * 9000000000000000000000)}`,
      cardNumber: `${prefix}${Math.floor(100 + Math.random() * 900)}-XXXX-XXXX-${Math.floor(1000 + Math.random() * 9000)}`,
      txId: `TXN-${Math.floor(10000 + Math.random() * 90000)}`,
      merchantId: `MER-${Math.floor(10000 + Math.random() * 90000)}`,
      merchantName: MERCHANT_NAMES[Math.floor(Math.random() * MERCHANT_NAMES.length)],
      amount: Math.floor(1000 + Math.random() * 499000),
      reasonCode: ['4837', '4853', '4855', '4834'][Math.floor(Math.random() * 4)],
      type: ['Retrieval', 'Chargeback', 'Fraud'][Math.floor(Math.random() * 3)],
      status: 'Valid'
    });
  }
  return records;
};
const SchemeDisputeUpload: React.FC = () => {
  const [scheme, setScheme] = useState<string>('');
  const [isDragging, setIsDragging] = useState(false);
  const [file, setFile] = useState<File | null>(null);
  const [uploadStatus, setUploadStatus] = useState<'idle' | 'validating' | 'success' | 'error'>('idle');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [parsedRecords, setParsedRecords] = useState<any[]>([]);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const uploadHistory = [{
    id: 'SCH-UPL-001',
    date: '2023-10-26 10:15',
    scheme: 'Visa',
    filename: 'VROL_Disputes_Oct26.csv',
    records: 45,
    status: 'Completed',
    errors: 0
  }, {
    id: 'SCH-UPL-002',
    date: '2023-10-25 14:30',
    scheme: 'Mastercard',
    filename: 'Mastercom_CB_Q3.xml',
    records: 28,
    status: 'Completed with Errors',
    errors: 2
  }, {
    id: 'SCH-UPL-003',
    date: '2023-10-24 11:00',
    scheme: 'UnionPay',
    filename: 'UPI_Retrievals_Oct.csv',
    records: 12,
    status: 'Completed',
    errors: 0
  }, {
    id: 'SCH-UPL-004',
    date: '2023-10-23 09:00',
    scheme: 'PayPak',
    filename: 'PayPak_Disputes_Oct.csv',
    records: 5,
    status: 'Completed',
    errors: 0
  }];
  const columns = [{
    header: 'Upload ID',
    accessor: 'id',
    className: 'font-medium text-gray-900 dark:text-white'
  }, {
    header: 'Date & Time',
    accessor: 'date'
  }, {
    header: 'Scheme',
    accessor: 'scheme',
    className: 'font-semibold text-blue-600 dark:text-blue-400'
  }, {
    header: 'File Name',
    accessor: 'filename'
  }, {
    header: 'Total Records',
    accessor: 'records'
  }, {
    header: 'Errors',
    accessor: (row: any) => row.errors > 0 ? <span className="text-red-600 font-medium" data-id="element-4109">{row.errors}</span> : '0'
  }, {
    header: 'Status',
    accessor: (row: any) => <StatusBadge status={row.status === 'Completed' ? 'success' : 'warning'} text={row.status} data-id="element-4110" />
  }];
  const parsedColumns = [{
    header: 'Row #',
    accessor: 'rowNum'
  }, {
    header: 'Case ID',
    accessor: 'caseId',
    className: 'font-mono text-xs'
  }, {
    header: 'ARN',
    accessor: 'arn',
    className: 'font-mono text-xs'
  }, {
    header: 'Card Number',
    accessor: 'cardNumber',
    className: 'font-mono text-xs'
  }, {
    header: 'TXN ID',
    accessor: 'txId'
  }, {
    header: 'Merchant',
    accessor: (row: any) => `${row.merchantName} (${row.merchantId})`
  }, {
    header: 'Amount',
    accessor: (row: any) => `PKR ${row.amount.toLocaleString()}`
  }, {
    header: 'Type',
    accessor: 'type'
  }, {
    header: 'Status',
    accessor: (row: any) => <StatusBadge status="success" text={row.status} data-id="element-4111" />
  }];
  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    if (scheme) setIsDragging(true);
  };
  const handleDragLeave = () => {
    setIsDragging(false);
  };
  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    if (!scheme) {
      alert('Please select a scheme first.');
      return;
    }
    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      handleFileSelect(e.dataTransfer.files[0]);
    }
  };
  const handleFileSelect = (selectedFile: File) => {
    if (!scheme) {
      alert('Please select a scheme first.');
      if (fileInputRef.current) fileInputRef.current.value = '';
      return;
    }
    // Basic validation based on scheme
    const isCsv = selectedFile.type === 'text/csv' || selectedFile.name.endsWith('.csv');
    const isXml = selectedFile.type === 'text/xml' || selectedFile.name.endsWith('.xml');
    if (scheme === 'Visa' && isCsv || scheme === 'Mastercard' && (isCsv || isXml) || scheme === 'UnionPay' && isCsv || scheme === 'PayPak' && isCsv) {
      setFile(selectedFile);
      setUploadStatus('idle');
    } else {
      alert(`Invalid file format for ${scheme}. Please check the requirements.`);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };
  const handleUpload = () => {
    if (!file || !scheme) return;
    setUploadStatus('validating');
    setTimeout(() => {
      setParsedRecords(generateMockRecords(scheme, Math.floor(8 + Math.random() * 5)));
      setIsModalOpen(true);
      setUploadStatus('idle');
    }, 1500);
  };
  const handleConfirmCreate = () => {
    setIsModalOpen(false);
    setUploadStatus('success');
  };
  const handleCancelCreate = () => {
    setIsModalOpen(false);
    setUploadStatus('idle');
  };
  return <div className="space-y-6" data-id="element-4112">
      <div data-id="element-4113">
        <h1 className="text-2xl font-bold text-gray-900 dark:text-white" data-id="element-4114">
          Scheme Dispute Ingestion
        </h1>
        <p className="mt-1 text-sm text-gray-500 dark:text-gray-400" data-id="element-4115">
          Upload dispute and chargeback files received directly from card
          schemes (Visa, Mastercard, UnionPay, PayPak).
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6" data-id="element-4116">
        <div className="lg:col-span-2 space-y-6" data-id="element-4117">
          <Card title="1. Select Scheme" data-id="element-4118">
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4" data-id="element-4119">
              {['Visa', 'Mastercard', 'UnionPay', 'PayPak'].map(s => <button key={s} onClick={() => {
              setScheme(s);
              setFile(null);
              setUploadStatus('idle');
            }} className={`flex flex-col items-center justify-center p-4 rounded-xl border-2 transition-all ${scheme === s ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-300' : 'border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-600 dark:text-gray-400 hover:border-blue-300 hover:bg-blue-50/50 dark:hover:bg-blue-900/10'}`} data-id="element-4120">
                  <img src={SCHEME_LOGOS[s]} alt={s} className="h-10 object-contain mb-2" data-id="element-4121" />
                  <span className="font-medium" data-id="element-4122">{s}</span>
                </button>)}
            </div>
          </Card>

          <Card title="2. Upload Scheme File" data-id="element-4123">
            {!scheme ? <div className="text-center py-12 bg-gray-50 dark:bg-gray-800/50 rounded-lg border border-gray-200 dark:border-gray-700" data-id="element-4124">
                <AlertCircleIcon className="mx-auto h-10 w-10 text-gray-400 mb-3" data-id="element-4125" />
                <p className="text-gray-500 dark:text-gray-400 font-medium" data-id="element-4126">
                  Please select a scheme above to continue.
                </p>
              </div> : !file ? <div className={`mt-2 flex justify-center px-6 pt-10 pb-12 border-2 border-dashed rounded-lg transition-colors ${isDragging ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20' : 'border-gray-300 dark:border-gray-600 hover:border-gray-400 dark:hover:border-gray-500'}`} onDragOver={handleDragOver} onDragLeave={handleDragLeave} onDrop={handleDrop} data-id="element-4127">
                <div className="space-y-1 text-center" data-id="element-4128">
                  <UploadCloudIcon className="mx-auto h-12 w-12 text-gray-400" data-id="element-4129" />
                  <div className="flex text-sm text-gray-600 dark:text-gray-400 justify-center" data-id="element-4130">
                    <label htmlFor="file-upload" className="relative cursor-pointer bg-white dark:bg-gray-800 rounded-md font-medium text-blue-600 dark:text-blue-400 hover:text-blue-500 focus-within:outline-none focus-within:ring-2 focus-within:ring-offset-2 focus-within:ring-blue-500 px-1" data-id="element-4131">
                      <span data-id="element-4132">Upload {scheme} file</span>
                      <input id="file-upload" name="file-upload" type="file" className="sr-only" accept={scheme === 'Mastercard' ? '.csv,.xml' : '.csv'} ref={fileInputRef} onChange={e => e.target.files && handleFileSelect(e.target.files[0])} data-id="element-4133" />
                    </label>
                    <p className="pl-1" data-id="element-4134">or drag and drop</p>
                  </div>
                  <p className="text-xs text-gray-500" data-id="element-4135">
                    {scheme === 'Mastercard' ? 'CSV or XML' : 'CSV'} files up to
                    20MB
                  </p>
                </div>
              </div> : <div className="mt-2" data-id="element-4136">
                <div className="flex items-center justify-between p-4 border border-gray-200 dark:border-gray-700 rounded-lg bg-gray-50 dark:bg-gray-900/50" data-id="element-4137">
                  <div className="flex items-center" data-id="element-4138">
                    <FileIcon className="h-8 w-8 text-blue-500 mr-3" data-id="element-4139" />
                    <div data-id="element-4140">
                      <p className="text-sm font-medium text-gray-900 dark:text-white" data-id="element-4141">
                        {file.name}
                      </p>
                      <p className="text-xs text-gray-500" data-id="element-4142">
                        {(file.size / 1024).toFixed(2)} KB • {scheme} Format
                      </p>
                    </div>
                  </div>
                  {uploadStatus === 'idle' && <button onClick={() => {
                setFile(null);
                if (fileInputRef.current) fileInputRef.current.value = '';
              }} className="text-gray-400 hover:text-red-500 transition-colors" data-id="element-4143">
                      <XIcon className="h-5 w-5" data-id="element-4144" />
                    </button>}
                </div>

                {uploadStatus === 'validating' && <div className="mt-4 p-4 bg-blue-50 dark:bg-blue-900/20 rounded-md flex items-center" data-id="element-4145">
                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-blue-600 mr-3" data-id="element-4146"></div>
                    <div data-id="element-4147">
                      <p className="text-sm font-medium text-blue-800 dark:text-blue-300" data-id="element-4148">
                        Applying {scheme} Validation Rules...
                      </p>
                      <p className="text-xs text-blue-600 dark:text-blue-400 mt-1" data-id="element-4149">
                        Checking ARN, transaction references, and mandatory
                        fields.
                      </p>
                    </div>
                  </div>}

                {uploadStatus === 'success' && <div className="mt-4 p-4 bg-green-50 dark:bg-green-900/20 rounded-md border border-green-200 dark:border-green-800" data-id="element-4150">
                    <div className="flex" data-id="element-4151">
                      <CheckCircleIcon className="h-5 w-5 text-green-600 dark:text-green-400 mr-3" data-id="element-4152" />
                      <div data-id="element-4153">
                        <h3 className="text-sm font-medium text-green-800 dark:text-green-300" data-id="element-4154">
                          Upload Successful
                        </h3>
                        <p className="mt-1 text-sm text-green-700 dark:text-green-400" data-id="element-4155">
                          Successfully parsed {parsedRecords.length} {scheme}{' '}
                          records. Cases have been created and merchants
                          notified.
                        </p>
                        <button onClick={() => {
                    setFile(null);
                    setUploadStatus('idle');
                  }} className="mt-3 text-sm font-medium text-green-800 dark:text-green-300 hover:underline" data-id="element-4156">
                          Upload another file
                        </button>
                      </div>
                    </div>
                  </div>}

                {uploadStatus === 'idle' && <div className="mt-6 flex justify-end" data-id="element-4157">
                    <button onClick={handleUpload} className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700" data-id="element-4158">
                      Process {scheme} File
                    </button>
                  </div>}
              </div>}
          </Card>
        </div>

        <div className="lg:col-span-1" data-id="element-4159">
          <Card title="Scheme Requirements" data-id="element-4160">
            {!scheme ? <p className="text-sm text-gray-500 dark:text-gray-400 italic" data-id="element-4161">
                Select a scheme to view specific file requirements.
              </p> : <div className="space-y-4" data-id="element-4162">
                <div className="flex items-start" data-id="element-4163">
                  <AlertCircleIcon className="h-5 w-5 text-blue-500 mt-0.5 mr-2 flex-shrink-0" data-id="element-4164" />
                  <p className="text-sm text-gray-600 dark:text-gray-400" data-id="element-4165">
                    Using active template:{' '}
                    <span className="font-semibold" data-id="element-4166">{scheme} V2.1</span>
                  </p>
                </div>

                <div data-id="element-4167">
                  <h4 className="text-sm font-medium text-gray-900 dark:text-white mb-2" data-id="element-4168">
                    Required Fields:
                  </h4>
                  <ul className="list-disc pl-5 text-sm text-gray-600 dark:text-gray-400 space-y-1" data-id="element-4169">
                    <li data-id="element-4170">Scheme Case ID</li>
                    <li data-id="element-4171">ARN (Acquirer Ref)</li>
                    <li data-id="element-4172">RRN (Retrieval Ref)</li>
                    <li data-id="element-4173">Transaction ID</li>
                    <li data-id="element-4174">Dispute Amount</li>
                    <li data-id="element-4175">Reason Code</li>
                    <li data-id="element-4176">Dispute Stage</li>
                  </ul>
                </div>

                <div className="pt-4 border-t border-gray-200 dark:border-gray-700" data-id="element-4177">
                  <button className="text-sm text-blue-600 dark:text-blue-400 hover:underline flex items-center" data-id="element-4178">
                    <DownloadIcon className="h-4 w-4 mr-1" data-id="element-4179" />
                    Download {scheme} Reference Template
                  </button>
                </div>
              </div>}
          </Card>
        </div>
      </div>

      <Card title="Recent Scheme Uploads" data-id="element-4180">
        <Table columns={columns} data={uploadHistory} keyField="id" pagination={false} data-id="element-4181" />
      </Card>

      <Modal isOpen={isModalOpen} onClose={handleCancelCreate} title={`Parsed Records from ${file?.name || 'File'}`} size="xl" data-id="element-4182">
        <div className="space-y-4" data-id="element-4183">
          <p className="text-sm text-gray-600 dark:text-gray-400" data-id="element-4184">
            Successfully parsed {parsedRecords.length} records. Please review
            before creating disputes.
          </p>
          <div className="max-h-[60vh] overflow-y-auto" data-id="element-4185">
            <Table columns={parsedColumns} data={parsedRecords} keyField="rowNum" pagination={false} data-id="element-4186" />
          </div>
          <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200 dark:border-gray-700" data-id="element-4187">
            <button onClick={handleCancelCreate} className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm text-sm font-medium text-gray-700 dark:text-gray-200 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700" data-id="element-4188">
              Cancel
            </button>
            <button onClick={handleConfirmCreate} className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700" data-id="element-4189">
              Confirm & Create Disputes
            </button>
          </div>
        </div>
      </Modal>
    </div>;
};
export default SchemeDisputeUpload;